import { readJson, removeKey, writeJson } from './safe-storage';

/**
 * Access tokens of the customer's own bookings, kept only on this device and only
 * for this studio. They are the customer's keys; the server stores only hashes.
 */
export interface DeviceBooking {
  token: string;
  bookingId: string;
  savedAt: string;
}

const key = (slug: string) => `plansgx:${slug}:bookings`;
const MAX = 20;

export function listDeviceBookings(slug: string): DeviceBooking[] {
  const items = readJson<DeviceBooking[]>(key(slug), []);
  return Array.isArray(items) ? items.filter((i) => typeof i?.token === 'string') : [];
}

export function saveDeviceBooking(slug: string, entry: Omit<DeviceBooking, 'savedAt'>) {
  const items = listDeviceBookings(slug).filter((i) => i.token !== entry.token);
  items.unshift({ ...entry, savedAt: new Date().toISOString() });
  writeJson(key(slug), items.slice(0, MAX));
}

export function forgetDeviceBooking(slug: string, token: string) {
  writeJson(
    key(slug),
    listDeviceBookings(slug).filter((i) => i.token !== token),
  );
}

/** Imports a booking opened from a shared link: /s/<slug>/my#t=<token>. */
export function importTokenFromHash(slug: string): boolean {
  const match = /(?:^|[#&])t=([A-Za-z0-9_-]{43,128})/.exec(window.location.hash);
  if (!match?.[1]) return false;
  const token = match[1];
  if (!listDeviceBookings(slug).some((i) => i.token === token)) {
    saveDeviceBooking(slug, { token, bookingId: '' });
  }
  history.replaceState(history.state, '', window.location.pathname + window.location.search);
  return true;
}

export function bookingShareLink(slug: string, token: string) {
  return `${window.location.origin}/s/${slug}/my#t=${token}`;
}

// Contacts remembered for the next booking on this device (the customer's own data).
export interface SavedContacts {
  name: string;
  phone: string;
  car: string;
}

const contactsKey = 'plansgx:contacts';
export const loadContacts = () => readJson<SavedContacts | null>(contactsKey, null);
export const saveContacts = (contacts: SavedContacts) => writeJson(contactsKey, contacts);
export const forgetContacts = () => removeKey(contactsKey);
